import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Calendar } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { getBlog } from "@/lib/api";
import { IMG } from "@/lib/content";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { getBlog().then(setPosts).catch(() => {}).finally(() => setLoading(false)); }, []);

  const [featured, ...rest] = posts;

  return (
    <>
      <PageHero testid="blog-hero" kicker="Blog & Stories" title="Stories of hope, service, and second chances" subtitle="Read what God is doing through Greater Faith — and be inspired to join the work." image={IMG.christmas} />

      <section className="py-20 sm:py-28 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && <p className="text-stone text-center py-20" data-testid="blog-loading">Loading stories...</p>}

          {featured && (
            <Reveal>
              <Link to={`/blog/${featured.slug}`} className="group grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-20" data-testid="blog-featured">
                <div className="img-zoom rounded-2xl shadow-xl h-80 lg:h-[420px]">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover rounded-2xl" />
                </div>
                <div>
                  <span className="text-gold-dark text-xs tracking-[0.2em] uppercase font-semibold">{featured.category}</span>
                  <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-plum mt-3 leading-tight group-hover:text-plum-dark transition-colors">{featured.title}</h2>
                  <p className="text-stone mt-4 leading-relaxed text-lg">{featured.excerpt}</p>
                  <div className="flex items-center gap-5 text-sm text-stone mt-5">
                    <span className="flex items-center gap-1.5"><Calendar size={15} />{fmtDate(featured.date)}</span>
                    <span className="flex items-center gap-1.5"><Clock size={15} />{featured.read_minutes} min read</span>
                  </div>
                  <span className="inline-flex items-center gap-2 text-plum font-medium mt-6 border-b-2 border-gold pb-1 group-hover:gap-3 transition-all">Read story <ArrowRight size={17} /></span>
                </div>
              </Link>
            </Reveal>
          )}

          <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rest.map((p) => (
              <StaggerItem key={p.slug}>
                <Link to={`/blog/${p.slug}`} className="group block bg-white rounded-2xl overflow-hidden border border-plum/10 lift h-full" data-testid={`blog-card-${p.slug}`}>
                  <div className="img-zoom h-52"><img src={p.image} alt={p.title} className="w-full h-full object-cover" /></div>
                  <div className="p-6">
                    <span className="text-gold-dark text-xs tracking-[0.18em] uppercase font-semibold">{p.category}</span>
                    <h3 className="font-heading text-2xl text-plum font-semibold mt-2 leading-snug group-hover:text-plum-dark transition-colors">{p.title}</h3>
                    <p className="text-stone text-sm mt-3 leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <div className="flex items-center gap-4 text-xs text-stone mt-4">
                      <span className="flex items-center gap-1.5"><Calendar size={13} />{fmtDate(p.date)}</span>
                      <span className="flex items-center gap-1.5"><Clock size={13} />{p.read_minutes} min</span>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </>
  );
}
